import { isProductEnabled } from "@/business/product-access";
import { isAdminOrOwner } from "@/user/utils";

/**
 * The feature's public surface. The nav bar and the route guard need to know
 * where profitability lives and who may see it; nothing else in this module is
 * theirs to import.
 */
export const PROFITABILITY_PATH = "/job-costing";

export const LEGACY_REPORTS_PATH = `${PROFITABILITY_PATH}/service-titan`;

export const profitabilityRunPath = (runId: string) =>
  `${PROFITABILITY_PATH}/${runId}`;

export const PROFITABILITY_AGENTS_PATH = `${PROFITABILITY_PATH}/agents`;

export const PROFITABILITY_NEW_TEMPLATE_PATH = `${PROFITABILITY_AGENTS_PATH}/new`;

export const profitabilityTemplatePath = (reportId: string) =>
  `${PROFITABILITY_AGENTS_PATH}/${reportId}`;

/** Under `pane/` so a pane id never collides with a record tab or a fixed one. */
export const profitabilityTemplatePanePath = (
  reportId: string,
  paneId: string,
) =>
  `${profitabilityTemplatePath(reportId)}/pane/${encodeURIComponent(paneId)}`;

export const profitabilityTemplateSettingsPath = (
  reportId: string,
  section?: string,
) =>
  `${profitabilityTemplatePath(reportId)}/settings${section ? `/${section}` : ""}`;

export const profitabilityRulePath = (
  reportId: string,
  ruleId: string,
  runId?: string | null,
) =>
  `${profitabilityTemplateSettingsPath(reportId, "rules")}/${encodeURIComponent(ruleId)}${runId ? `?period=${encodeURIComponent(runId)}` : ""}`;

/**
 * `?period=` is the run. `section` is `dashboard`, `review`, `insights`, `workbook`,
 * `documents`, a record tab, or `pane/<id>`.
 */
export const profitabilityPeriodPath = (
  reportId: string,
  runId: string,
  section?: string,
  search = "",
) => {
  const params = new URLSearchParams(search);
  params.set("period", runId);
  return `${profitabilityTemplatePath(reportId)}${section ? `/${section}` : ""}?${params.toString()}`;
};

/** Query of the upload page: `?start=&end=` (ISO dates), `?input=`, `?from=`. */
export interface NewRunPreset {
  start_date: string;
  end_date: string;
  input?: string | null;
  from?: string | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const profitabilityNewRunPath = (
  reportId: string,
  preset?: NewRunPreset | null,
) => {
  const path = `${profitabilityTemplatePath(reportId)}/runs/new`;
  if (!preset) return path;
  const params = new URLSearchParams({
    start: preset.start_date,
    end: preset.end_date,
  });
  if (preset.input) params.set("input", preset.input);
  if (preset.from) params.set("from", preset.from);
  return `${path}?${params.toString()}`;
};

export const readNewRunPreset = (
  search: URLSearchParams,
): NewRunPreset | null => {
  const start = search.get("start");
  const end = search.get("end");
  if (!start || !end || !ISO_DATE.test(start) || !ISO_DATE.test(end))
    return null;
  if (start > end) return null;
  return {
    start_date: start,
    end_date: end,
    input: search.get("input") || null,
    from: search.get("from") || null,
  };
};

export const PROFITABILITY_ROUTE_PATTERNS = [
  LEGACY_REPORTS_PATH,
  // Its own route, so `agents` is never read as a run id below.
  PROFITABILITY_AGENTS_PATH,
  `${PROFITABILITY_NEW_TEMPLATE_PATH}/form`,
  `${PROFITABILITY_AGENTS_PATH}/:reportId/runs/new`,
  `${PROFITABILITY_AGENTS_PATH}/:reportId/pane/:pane`,
  `${PROFITABILITY_AGENTS_PATH}/:reportId/settings/:settingsSection/:ruleId`,
  `${PROFITABILITY_AGENTS_PATH}/:reportId/settings/:settingsSection`,
  `${PROFITABILITY_AGENTS_PATH}/:reportId/:section?`,
  `${PROFITABILITY_PATH}/:runId?/:pane?`,
];

/** The search parameter that holds Edit agent open beside an agent's page. */
export const EDIT_PARAM = "edit";

/** The agent's page with Edit agent (its conversation) open beside it. */
export const profitabilityTemplateEditPath = (reportId: string) =>
  `${profitabilityTemplatePath(reportId)}?${EDIT_PARAM}=1`;

export const isProfitabilityPath = (path: string) =>
  path === PROFITABILITY_PATH || path.startsWith(`${PROFITABILITY_PATH}/`);

/**
 * Mirrors `User#can_manage_finances?` on the API: who manages every template.
 * Keep the two in step: a looser rule here shows controls that 403, a tighter
 * one hides a report the user is entitled to.
 */
export const canManageProfitability = (
  user: { role?: string; parentable_type?: string } | null | undefined,
) =>
  !!user &&
  (user.parentable_type?.toUpperCase() === "TENANT" ||
    isAdminOrOwner(user.role));

/**
 * Mirrors `Profitability::Access` on the API, which is what actually gates
 * these endpoints: finance, or a manager an owner shared at least one template
 * with (the business's `profitability_access`).
 */
export const canAccessProfitability = (
  user: { role?: string; parentable_type?: string } | null | undefined,
  business?: { profitability_access?: "manage" | "view" | null } | null,
) => canManageProfitability(user) || !!business?.profitability_access;

/**
 * Whether the tab can open reports. Access is not enough: a business with no
 * published report script has nothing behind the tab, and the empty state would
 * invite a report the API then refuses.
 */
export const canOpenProfitabilityReports = (
  user: { role?: string; parentable_type?: string } | null | undefined,
  business:
    | {
        has_published_profitability_script?: boolean;
        enabled_products?: unknown;
        profitability_access?: "manage" | "view" | null;
        profitability_workflows?: (1 | 2)[];
      }
    | null
    | undefined,
  documentsOnly = false,
) =>
  isProductEnabled(business?.enabled_products, "profitability") &&
  canAccessProfitability(user, business) &&
  (documentsOnly ||
    (canManageProfitability(user) &&
      !!business?.has_published_profitability_script &&
      (!business.profitability_workflows ||
        business.profitability_workflows.includes(1))));
